import type { GiftLinksAddBlock } from "@/components/giftLinks/types";

/**
 * GFG-405 — every user-visible string of Manage Gift Links. Money arguments are already
 * formatted (`formatUsd`, "$25.00"): the copy never prefixes a currency sign.
 */
export const GIFT_LINKS_COPY = {
  menu: { manage: "Manage Gift Links", preview: "Preview Gift Links" },
  title: "Gift Links",
  closeLabel: "Close",
  subtitle: {
    all: (campaign: string, total: number) =>
      `${campaign} · ${total.toLocaleString()} ${total === 1 ? "link" : "links"}`,
    filtered: (campaign: string, shown: number, total: number) =>
      `${campaign} · ${shown.toLocaleString()} of ${total.toLocaleString()} ${total === 1 ? "link" : "links"}`,
  },
  banner: {
    activates: (date: string) => `Links activate ${date}`,
    activeSince: (date: string) => `Links active since ${date}`,
    fallback: "These links activate when the campaign is scheduled.",
    /** Between the dated sentence and the distribute sentence (which opens with a space). */
    join: " ·",
    distribute:
      " You distribute these yourself — Gifts for Good sends no emails for this campaign.",
  },
  searchPlaceholder: "Search links",
  searchLabel: "Search links",
  allStatuses: "All statuses",
  statusLabel: "Status",
  columns: { number: "#", link: "Gift link", status: "Status", redeemed: "Redeemed" },
  /** The CSV header row, in column order. */
  csvHeaders: ["#", "Gift Link", "Status", "Redeemed"] as const,
  emptyValue: "—",
  empty: "No links match.",
  loadingLabel: "Loading gift links",
  errorFallback: "Couldn't load the gift links for this campaign.",
  copyOne: "Copy this gift link",
  copyOneLabel: (n: number) => `Copy gift link ${n}`,
  toast: {
    copied: "Gift link copied to clipboard",
    copiedAll: (n: number) => `${n.toLocaleString()} gift ${n === 1 ? "link" : "links"} copied`,
    nothingToCopy: "No gift links to copy",
    copyFailure:
      "Couldn't copy — your browser blocked clipboard access. Select the link to copy it.",
    dismiss: "Dismiss",
  },
  footer: {
    close: "Close",
    add: "Add gift links",
    copyAll: "Copy all links",
    exportCsv: "Export CSV",
  },
  budgetStrip: {
    budgeted: (n: number) => `${n.toLocaleString()} ${n === 1 ? "link" : "links"} budgeted`,
    free: (n: number) => `${n.toLocaleString()} ${n === 1 ? "space" : "spaces"} free`,
    perLink: (price: string) => `${price} per link`,
    separator: " · ",
    history: "Budget history",
  },
  /** Why Add gift links is disabled, one reason per `GiftLinksAddBlock`. */
  addBlocked: {
    viewer: "You have view-only access to this campaign, so you can't add gift links.",
    standard_send: "Gift links can only be added to a manual distribution campaign.",
    draft:
      "This campaign hasn't launched yet — set its number of gift links in the campaign builder.",
    closed: "This campaign has ended, so gift links can't be added.",
    paused: "This campaign is paused. Unpause it to add gift links.",
    multi_use: "This campaign uses one shared gift link, so links can't be added to it.",
    no_budget: "This campaign has no budget yet, so gift links can't be added.",
    no_free_space: "Gift links can only fill freed spaces, and this campaign has none.",
  } satisfies Record<GiftLinksAddBlock, string>,
  /** Why Copy all links / Export CSV is disabled for someone who can't edit the campaign. */
  exportBlocked:
    "You have view-only access to this campaign, so you can't copy or export gift links.",
  wizard: {
    title: "Add gift links",
    subtitle: (campaign: string, price: string) => `${campaign} · ${price} per gift`,
    quantityLabel: "How many links?",
    stepper: {
      fewer: "One fewer",
      more: "One more",
      input: "Number of gift links",
    },
    costFree: (free: number) =>
      `${free.toLocaleString()} into free ${free === 1 ? "space" : "spaces"} · `,
    paymentTitle: (n: number, campaign: string) =>
      `Add ${n.toLocaleString()} gift ${n === 1 ? "link" : "links"} to ${campaign}`,
    paymentSub: "Payment details",
    lineNewLinks: "New gift links",
    actions: {
      cancel: "Cancel",
      continueToPayment: "Continue to payment",
      continue: "Continue",
      add: "Add gift links",
      back: "Back",
    },
    added: (n: number, campaign: string) =>
      `${n.toLocaleString()} gift ${n === 1 ? "link" : "links"} added to ${campaign}`,
    quoteError: "Couldn't price these gift links. Try again.",
  },
  /** AM4: the credits-shortfall and credit-limit texts, worded for gift links. */
  credits: {
    askAdmin: (credits: string, usd: string) =>
      `Your wallet needs ${credits} more credits (${usd}) for these gift links. Ask an account admin to add credits in the Wallet, then try again.`,
    lotExpiry: (purchaseExpiry: string, campaignClose: string) =>
      `Your wallet doesn't have enough credits for these gift links, and buying more won't help: credits bought today expire on ${purchaseExpiry}, but this campaign runs until ${campaignClose}. Credits must last until the campaign ends.`,
    lotExpiryCanAdd: (n: number) =>
      `You can add up to ${n.toLocaleString()} ${n === 1 ? "gift link" : "gift links"} without buying credits.`,
  },
  creditLimit: (more: string, limit: string, headroom: string) =>
    `These gift links need ${more} more exposure, but your credit limit is ${limit} and only ${headroom} is left. Add fewer gift links or contact us to raise the limit.`,
} as const;
